import { Fragment, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Phone, ShoppingBag, Star } from "react-feather";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import DataTable from "react-data-table-component";
import {
  User,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  Package,
  XCircle,
  X,
  Copy
} from "react-feather";
import {
  Col,
  Row,
  Card,
  CardBody,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Container,
} from "reactstrap";
import { H3 } from "../../../../AbstractElements";
import { FaArrowLeft, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { baseURL } from "../../../../Services/api/baseURL";
import axios from "axios";
import { toast } from "react-toastify";
import LoyaltyHistory from "./LoyaltyHistory";
import { BarChart3, Tag } from "lucide-react";
import TopBoughtProducts from "./TopBoughtProducts";
import TopBoughtCategorySubcategory from "./TopBoughtCategorySubcategory";
import CustomerTagModal from "./Tag/CustomerTagModal";

const CustomerDetailPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [ordersData, setOrdersData] = useState([]);
  const [cancelledOrdersData, setCancelledOrdersData] = useState([]);
  const [activeTab, setActiveTab] = useState("all");

  const [isEditing, setIsEditing] = useState(false);
  const [newPhone, setNewPhone] = useState(null);
  const [newEmail, setNewEmail] = useState(null);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  const inputRef = useRef(null)

    useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const [mainTab, setMainTab] = useState("orders");

  const [navigationData, setNavigationData] = useState({
    currentIndex: -1,
    totalCustomers: 0,
    customersList: [],
    filters: {},
  });

  const handleNavigate = (orderId, orderIndex) => {
    const currentOrdersList =
      activeTab === "cancelled" ? cancelledOrdersData : ordersData;

    navigate(`/orders/${orderId}`, {
      state: {
        currentIndex: orderIndex,
        totalOrders: currentOrdersList.length,
        ordersList: currentOrdersList,
        filters: {
          customerId: customerId,
          orderType: activeTab,
        },
        isCustomerContext: true,
        customerName: `${customerData?.firstName} ${
          customerData?.lastName || ""
        }`.trim(),
      },
    });
  };

  useEffect(() => {
    if (location.state) {
      setNavigationData({
        currentIndex:
          location.state.currentIndex !== undefined
            ? location.state.currentIndex
            : -1,
        totalCustomers: location.state.totalCustomers || 0,
        customersList: location.state.customersList || [],
        filters: location.state.filters || {},
      });
    }
  }, [location.state]);

  const handlePreviousCustomer = useCallback(async () => {
    const currentIndex = navigationData.currentIndex;
    if (currentIndex <= 0 || !navigationData.customersList.length) return;

    const previousCustomer = navigationData.customersList[currentIndex - 1];
    if (previousCustomer) {
      navigate(`/customers/customer-detail/${previousCustomer._id}`, {
        state: {
          ...location.state,
          currentIndex: currentIndex - 1,
        },
      });
    } else {
      try {
        const token = JSON.parse(localStorage.getItem("token"));
        const filters = navigationData.filters;
        const prevPage = Math.max(1, filters.page - 1);

        const response = await axios.get(
          `${baseURL}/api/customers/get-customers-list`,
          {
            params: {
              page: prevPage,
              limit: filters.limit,
              search: filters.search || "",
            },
            headers: {
              Authorization: `${token}`,
            },
          }
        );

        if (response.status === 200) {
          const prevPageCustomers = response?.data?.data?.customerList;
          const lastCustomerOnPrevPage =
            prevPageCustomers[prevPageCustomers.length - 1];

          if (lastCustomerOnPrevPage) {
            navigate(
              `/customers/customer-detail/${lastCustomerOnPrevPage._id}`,
              {
                state: {
                  ...location.state,
                  currentIndex: currentIndex - 1,
                  customersList: prevPageCustomers,
                },
              }
            );
          }
        }
      } catch (error) {
        console.error("Error fetching previous page:", error);
        toast.error("Failed to navigate to previous customer");
      }
    }
  }, [navigationData, navigate, location.state]);

  const handleNextCustomer = useCallback(async () => {
    const currentIndex = navigationData.currentIndex;
    if (
      currentIndex >= navigationData.totalCustomers - 1 ||
      !navigationData.customersList.length
    )
      return;

    const nextCustomer = navigationData.customersList[currentIndex + 1];
    if (nextCustomer) {
      navigate(`/customers/customer-detail/${nextCustomer._id}`, {
        state: {
          ...location.state,
          currentIndex: currentIndex + 1,
        },
      });
    } else {
      try {
        const token = JSON.parse(localStorage.getItem("token"));
        const filters = navigationData.filters;
        const nextPage = filters.page + 1;

        const response = await axios.get(
          `${baseURL}/api/customers/get-customers-list`,
          {
            params: {
              page: nextPage,
              limit: filters.limit,
              search: filters.search || "",
            },
            headers: {
              Authorization: `${token}`,
            },
          }
        );

        if (response.status === 200) {
          const nextPageCustomers = response?.data?.data?.customerList;
          const firstCustomerOnNextPage = nextPageCustomers[0];

          if (firstCustomerOnNextPage) {
            navigate(
              `/customers/customer-detail/${firstCustomerOnNextPage._id}`,
              {
                state: {
                  ...location.state,
                  currentIndex: currentIndex + 1,
                  customersList: nextPageCustomers,
                },
              }
            );
          }
        }
      } catch (error) {
        console.error("Error fetching next page:", error);
        toast.error("Failed to navigate to next customer");
      }
    }
  }, [navigationData, navigate, location.state]);

  const navigationInfo = useMemo(() => {
    const { currentIndex, totalCustomers } = navigationData;

    return {
      showNavigation: currentIndex !== -1 && totalCustomers > 1,
      canGoPrevious: currentIndex > 0,
      canGoNext: currentIndex < totalCustomers - 1,
      currentPosition: currentIndex + 1,
      totalCount: totalCustomers,
    };
  }, [navigationData]);

  const orderColumns = [
    {
      name: "Order ID",
      selector: (row) => row?.sequence_number,
      cell: (row, index) => (
        <div
          onClick={() => handleNavigate(row?._id, index)}
          className="fw-bold text-primary cursor-pointer"
          style={{ cursor: "pointer" }}
        >
          {row?.sequence_number || `#${row?._id?.slice(-8)}`}
        </div>
      ),
      sortable: true,
      minWidth: "140px",
    },
    {
      name: "Order Date",
      selector: (row) => row?.createdAt,
      cell: (row) => (
        <div>
          {row?.createdAt
            ? new Date(row.createdAt).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "N/A"}
        </div>
      ),
      sortable: true,
      minWidth: "120px",
    },
    {
      name: "Products",
      selector: (row) =>
        row?.products?.length || row?.order_Variants?.length || 0,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <Package size={14} className="me-1" />
          {row?.products?.length || row?.order_Variants?.length || 0}
        </div>
      ),
      sortable: true,
      center: true,
      minWidth: "100px",
    },
    {
      name: "Order Value",
      selector: (row) => row?.order_value,
      cell: (row) => (
        <div className="fw-bold text-success">
          ${row?.order_value ? row.order_value.toFixed(2) : "0.00"}
        </div>
      ),
      sortable: true,
      minWidth: "120px",
    },
    {
      name: "Order Status",
      selector: (row) => row?.order_status,
      cell: (row) => {
        const getStatusColor = (status) => {
          switch (status?.toLowerCase()) {
            case "pending":
              return { bg: "#fff3cd", color: "#856404" };
            case "confirmed":
              return { bg: "#d1ecf1", color: "#0c5460" };
            case "shipped":
              return { bg: "#cce5ff", color: "#004085" };
            case "delivered":
              return { bg: "#d4edda", color: "#155724" };
            case "cancelled":
              return { bg: "#f8d7da", color: "#721c24" };
            default:
              return { bg: "#e2e3e5", color: "#383d41" };
          }
        };

        const statusColors = getStatusColor(row?.order_status);

        return (
          <span
            style={{
              padding: "4px 12px",
              borderRadius: "12px",
              backgroundColor: statusColors.bg,
              color: statusColors.color,
              fontWeight: 500,
              fontSize: "0.85rem",
              textTransform: "capitalize",
            }}
          >
            {row?.order_status || "Unknown"}
          </span>
        );
      },
      sortable: true,
      center: true,
      minWidth: "120px",
    },
    {
      name: "Payment Status",
      selector: (row) => row?.transaction_status,
      cell: (row) => {
        const getPaymentStatusColor = (status) => {
          switch (status?.toLowerCase()) {
            case "completed":
              return { bg: "#d4edda", color: "#155724" };
            case "pending":
              return { bg: "#fff3cd", color: "#856404" };
            case "failed":
              return { bg: "#f8d7da", color: "#721c24" };
            default:
              return { bg: "#e2e3e5", color: "#383d41" };
          }
        };

        const statusColors = getPaymentStatusColor(row?.transaction_status);

        return (
          <span
            style={{
              padding: "4px 12px",
              borderRadius: "12px",
              backgroundColor: statusColors.bg,
              color: statusColors.color,
              fontWeight: 500,
              fontSize: "0.85rem",
              textTransform: "capitalize",
            }}
          >
            {row?.transaction_status || "N/A"}
          </span>
        );
      },
      sortable: true,
      center: true,
      minWidth: "130px",
    },
    {
      name: "Store",
      selector: (row) => row?.store?.storeName,
      cell: (row) => <div>{row?.store?.storeName || "N/A"}</div>,
      sortable: true,
      minWidth: "100px",
    },
    {
      name: "Delivery Partner",
      selector: (row) => row?.deliveryUser?.deliveryPartner?.full_name,
      cell: (row) => (
        <div>{row?.deliveryUser?.deliveryPartner?.full_name || "N/A"}</div>
      ),
      sortable: true,
      minWidth: "130px",
    },
  ];

  const fetchCustomerOrders = async (orderStatus = null) => {
    setOrdersLoading(true);
    try {
      const token = await JSON.parse(localStorage.getItem("token"));
      let url = `${baseURL}/api/order/get-customer-orders?userId=${customerId}`;
      if (orderStatus) {
        url += `&order_status=${orderStatus}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `${token}`,
        },
      });

      if (response.status === 200) {
        const orders = response?.data?.data || [];

        if (orderStatus === "cancelled") {
          setCancelledOrdersData(orders);
        } else {
          setOrdersData(orders);

          // Extract customer data from first order if available (only for all orders)
          if (orders.length > 0 && orders[0].address) {
            const firstOrder = orders[0];
            const mockCustomerData = {
              firstName: firstOrder.user.first_name,
              lastName: firstOrder.user.last_name,
              email: firstOrder.user.email || "N/A",
              address: {
                address: firstOrder.address.address,
                city: firstOrder.address.city,
                state: firstOrder.address.state,
                zipCode: firstOrder.address.addressPincode,
              },
              totalOrders: orders.length,
              totalAmount: orders.reduce(
                (sum, order) => sum + (order.order_value || 0),
                0
              ),
              lastOrderDate: orders[0].createdAt,
              isBlocked: false, // Default to false since we don't have this data
            };
            setCustomerData(mockCustomerData);
          }
        }
      }
    } catch (e) {
      console.log(e);
      toast.error(`Failed to fetch ${orderStatus ? "cancelled " : ""}orders`);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchCustomerInfo = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const response = await axios.get(
        `${baseURL}/api/customers/get-customerInfo/${customerId}`,
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      if (response.status === 200) {
        setCustomerInfo(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch customer info", error);
      toast.error("Failed to fetch customer details");
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchCustomerOrders(); // Fetch all orders initially
      fetchCustomerInfo();
    }
  }, [customerId]);

  const handleTabChange = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      if (tab === "cancelled") {
        fetchCustomerOrders("cancelled");
      } else {
        if (ordersData.length === 0) {
          fetchCustomerOrders();
        }
      }
    }
  };

  const handleGoBack = (userId) => {
    navigate(-1);
  };

  const handleSaveEdits = async (userId) => {

      const phoneRegex = /^(\+?61|0)[2-478]\d{8}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!phoneRegex.test(newPhone)) {
    toast.error("Please enter a valid phone number");
    return;
  }

  if (!emailRegex.test(newEmail)) {
    toast.error("Please enter a valid email address");
    return;
  }

    const token = JSON.parse(localStorage.getItem("token"));

    setCustomerInfo((prev) => ({ ...prev, phone: newPhone, email: newEmail }));

    try {
      await axios.patch(
        `${baseURL}/api/customers/update-userdata/${userId}`,
        { email: newEmail, phone: newPhone },
        { headers: { Authorization: token } }
      );
      setIsEditing(false);

      console.log("Address updated successfully!");
    } catch (err) {
      console.error("Error updating address:", err);

      fetchCustomerInfo();
    }
  };

  const handleTagsUpdated = () => {
    // Refresh customer info after tags are updated
    fetchCustomerInfo();
  };

  const handleRemoveTag = async (tagId) => {
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const customerId = customerInfo.id || customerInfo._id;

      // Get current tag IDs and remove the selected one
      const currentTagIds = customerInfo.tags.map(tag => tag._id);
      const updatedTagIds = currentTagIds.filter(id => id !== tagId);

      const response = await axios.put(
        `${baseURL}/api/tags/customers/${customerId}/tags`,
        { tagIds: updatedTagIds },
        { headers: { Authorization: token } }
      );

      if (response.status === 200) {
        toast.success("Tag removed successfully");
        fetchCustomerInfo(); // Refresh to show updated tags
      }
    } catch (error) {
      toast.error("Failed to remove tag");
      console.error(error);
    }
  };

  const handleCopyStripeId = () => {
    if (customerInfo?.stripeCustomerId) {
      navigator.clipboard.writeText(customerInfo.stripeCustomerId)
        .then(() => {
          toast.success("Stripe Customer ID copied to clipboard");
        })
        .catch(() => {
          toast.error("Failed to copy Stripe Customer ID");
        });
    }
  };
  return (
    <Fragment>
      <Container fluid>
        <div className="page-title">
          <Row className="align-items-center">
            <Col xs="12">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <Button
                    className="filter_btn me-3"
                    onClick={handleGoBack}
                    title="Back to Orders"
                  >
                    <FaArrowLeft color="#d422ad" />
                  </Button>
                  <H3 className="mb-0 fw-bold">Customer Details</H3>
                </div>

                <div className="d-flex align-items-center">
                  {navigationInfo.showNavigation && (
                    <div className="d-flex align-items-center me-3">
                      <Button
                        className="filter_btn me-2"
                        onClick={handlePreviousCustomer}
                        disabled={!navigationInfo.canGoPrevious}
                        title="Previous Order"
                      >
                        <FaChevronLeft
                          color={
                            !navigationInfo.canGoPrevious ? "#ccc" : "#d422ad"
                          }
                        />
                      </Button>
                      <span
                        className="mx-2 text-muted"
                        style={{ fontSize: "14px" }}
                      >
                        {navigationInfo.currentPosition} of{" "}
                        {navigationInfo.totalCount}
                      </span>
                      <Button
                        className="filter_btn me-2"
                        onClick={handleNextCustomer}
                        disabled={!navigationInfo.canGoNext}
                        title="Next Order"
                      >
                        <FaChevronRight
                          color={!navigationInfo.canGoNext ? "#ccc" : "#d422ad"}
                        />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Container>
      {/* Customer Profile Section */}
      <Row className="">
        <Col md={12}>
          <Card className="shadow-sm border-0">
            <CardBody className="p-4">
              <div className="d-flex align-items-start justify-content-between">
                <Row className="flex-grow-1">
                  {/* Left Column - Customer Info & Tags */}
                  <Col md={8}>
                  <div className="d-flex align-items-start">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                      style={{
                        width: "70px",
                        height: "70px",
                        backgroundColor: "#f8f9ff",
                        border: "3px solid #e8eaff",
                      }}
                    >
                      <User size={28} color="#6c5ce7" />
                    </div>
                    <div className="flex-grow-1">
                    <div className="flex gap-3 items-center">
                      <div>
                        <h4 className="mb-2 fw-bold text-dark">
                          {customerInfo?.name || ""}
                        </h4>
                      </div>
                      <div>
                        <span
                          className="badge px-3 py-2 mb-2"
                          style={{
                            backgroundColor: customerInfo?.isBlocked
                              ? "#fee2e2"
                              : "#dcfce7",
                            color: customerInfo?.isBlocked
                              ? "#dc2626"
                              : "#16a34a",
                            fontWeight: 600,
                            fontSize: "0.6rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {customerInfo?.isBlocked ? "Blocked" : "Active"}
                        </span>
                      </div>
                    </div>
                    <p>
                      id:{" "}
                      <span className="text-green-600 text-xs">
                        {customerInfo?.id}
                      </span>
                    </p>
                    <div className="mb-2">
                      <div className="d-flex align-items-center text-muted mb-1">
                        <Phone size={16} className="me-2 text-primary" />
                        {isEditing ? (
                          <input
                            value={newPhone}
                            ref={inputRef}
                            onChange={(e) => setNewPhone(e.target.value)}
                            className="text-sm"
                          />
                        ) : (
                          <span className="fw-medium">
                            {customerInfo?.phone || "No phone provided"}
                          </span>
                        )}
                      </div>
                      <div className="d-flex align-items-center text-muted mb-1">
                        <Mail size={16} className="me-2 text-primary" />
                        {isEditing ? (
                          <input
                            value={newEmail}
                            className="text-sm"
                            onChange={(e) => setNewEmail(e.target.value)}
                          />
                        ) : (
                          <span className="fw-medium">
                            {customerInfo?.email || "No email provided"}
                          </span>
                        )}
                      </div>
                      <div className="d-flex align-items-center text-muted">
                        <MapPin size={16} className="me-2 text-success" />
                        <span>
                          {customerInfo?.address[0]?.address || "N/A"}
                        </span>
                      </div>

                      {/* Tags Section */}
                      <div className="mt-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Tag size={16} className="text-info" />
                          <span className="fw-medium text-dark">Tags:</span>
                          <button
                            onClick={() => setIsTagModalOpen(true)}
                            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                            style={{
                              padding: "4px 12px",
                              fontSize: "0.75rem",
                              borderRadius: "6px",
                            }}
                          >
                            <Tag size={14} />
                            Manage Tags
                          </button>
                        </div>
                        {customerInfo?.tags && customerInfo.tags.length > 0 ? (
                          <div className="d-flex flex-wrap gap-2">
                            {customerInfo.tags.map((tag) => (
                              <span
                                key={tag._id}
                                className="badge d-flex align-items-center gap-1"
                                style={{
                                  backgroundColor: tag.color || "#6c757d",
                                  color: "#fff",
                                  padding: "6px 12px",
                                  fontSize: "0.8rem",
                                  fontWeight: "500",
                                  borderRadius: "6px",
                                }}
                              >
                                {tag.display_name}
                                <X
                                  size={14}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleRemoveTag(tag._id)}
                                  title="Remove tag"
                                />
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted small">No tags assigned</span>
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                </Col>

                {/* Right Column - Payment & Subscription */}
                <Col md={4}>
                  <div className="border-start ps-4 h-100">
                    <h6 className="fw-bold mb-3">Payment & Subscription</h6>

                    {/* Stripe Customer ID */}
                    <div className="mb-2">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <span className="fw-medium text-dark">Stripe Customer ID:</span>{" "}
                          <span className="text-muted small">
                            {customerInfo?.stripeCustomerId || "Not created"}
                          </span>
                        </div>
                        {customerInfo?.stripeCustomerId && (
                          <Copy
                            size={16}
                            className="text-primary"
                            style={{ cursor: "pointer" }}
                            onClick={handleCopyStripeId}
                            title="Copy Stripe Customer ID"
                          />
                        )}
                      </div>
                    </div>

                    {/* Email Marketing */}
                    <div className="mb-2">
                      <span className="fw-medium text-dark">Email Marketing:</span>{" "}
                      <span className={`badge ${customerInfo?.emailMarketingOptIn ? 'bg-success' : 'bg-secondary'}`}>
                        {customerInfo?.emailMarketingOptIn ? 'Opted In' : 'Opted Out'}
                      </span>
                    </div>

                    {/* SMS Marketing */}
                    <div className="mb-2">
                      <span className="fw-medium text-dark">SMS Marketing:</span>{" "}
                      <span className={`badge ${customerInfo?.smsMarketingOptIn ? 'bg-success' : 'bg-secondary'}`}>
                        {customerInfo?.smsMarketingOptIn ? 'Opted In' : 'Opted Out'}
                      </span>
                    </div>

                    {/* Mailchimp Status */}
                    <div className="mb-2">
                      <span className="fw-medium text-dark">Mailchimp Status:</span>{" "}
                      <span className={`badge ${
                        customerInfo?.mailchimpStatus === 'subscribed' ? 'bg-success' :
                        customerInfo?.mailchimpStatus === 'unsubscribed' ? 'bg-warning' :
                        'bg-secondary'
                      }`}>
                        {customerInfo?.mailchimpStatus || 'Not synced'}
                      </span>
                    </div>

                    {/* Consent Timestamp */}
                    {customerInfo?.consentTimestamp && (
                      <div className="mb-2">
                        <span className="fw-medium text-dark">Consent Date:</span>{" "}
                        <span className="text-muted small">
                          {new Date(customerInfo.consentTimestamp).toLocaleDateString()}
                        </span>
                      </div>
                    )}

                    {/* Mailchimp Subscribed Date */}
                    {customerInfo?.mailchimpSubscribedAt && (
                      <div className="mb-2">
                        <span className="fw-medium text-dark">Subscribed:</span>{" "}
                        <span className="text-muted small">
                          {new Date(customerInfo.mailchimpSubscribedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}

                    {/* Mailchimp Unsubscribed Date */}
                    {customerInfo?.mailchimpUnsubscribedAt && (
                      <div className="mb-2">
                        <span className="fw-medium text-dark">Unsubscribed:</span>{" "}
                        <span className="text-muted small">
                          {new Date(customerInfo.mailchimpUnsubscribedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </Col>
                </Row>

                {/* Edit Buttons on the Right */}
                <div className="d-flex items-center gap-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => {
                          handleSaveEdits(customerInfo?.id);
                        }}
                        className="flex bg-green-500 px-3 py-1 rounded-3 items-center"
                      >
                        <span
                          className="badge "
                          style={{
                            fontWeight: 600,
                            fontSize: "0.75rem",

                            letterSpacing: "0.05em",
                          }}
                        >
                          Save
                        </span>
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setNewPhone(customerInfo?.phone || "");
                          setNewEmail(customerInfo?.email || "");
                        }}
                        className="flex bg-red-500 px-3 py-1 rounded-3 items-center"
                      >
                        <span
                          className="badge "
                          style={{
                            fontWeight: 600,
                            fontSize: "0.75rem",

                            letterSpacing: "0.05em",
                          }}
                        >
                          Cancel
                        </span>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setIsEditing(true);
                        setNewPhone(customerInfo?.phone || "");
                        setNewEmail(customerInfo?.email || "");
                     
                      }}
                      className="flex bg-blue-500 px-3 py-1 rounded-3 items-center"
                    >
                      <span
                        className="badge "
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",

                          letterSpacing: "0.05em",
                        }}
                      >
                        Edit
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Nav pills className="gap-2 p-3 ">
          <NavItem>
            <NavLink
              className="px-4 py-2 rounded-lg "
              style={{
                cursor: "pointer",
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px 8px ",
                fontWeight: mainTab === "orders" ? "600" : "500",
                display: "flex",
                justifyContent: "center",
                gap: "8px",
                alignItems: "center",
                backgroundColor: mainTab === "orders" ? "#d3178a" : "#e2e8f0",
                color: mainTab === "orders" ? "#ffffff" : "#475569",
              }}
              onClick={() => setMainTab("orders")}
            >
              <ShoppingBag size={16} />
              <span>Orders</span>
            </NavLink>
          </NavItem>

          <NavItem>
            <NavLink
              className={`px-4 py-2 rounded-lg `}
              style={{
                cursor: "pointer",
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px 8px 8px 8px ",
                fontWeight: mainTab === "loyalty" ? "600" : "500",
                display: "flex",
                justifyContent: "center",
                gap: "8px",
                alignItems: "center",
                backgroundColor: mainTab === "loyalty" ? "#d3178a" : "#e2e8f0",
                color: mainTab === "loyalty" ? "#ffffff" : "#475569",
              }}
              onClick={() => setMainTab("loyalty")}
            >
              <Star size={16} />
              <span>Loyalty</span>
            </NavLink>
          </NavItem>
        </Nav>
      </Row>

      <TabContent activeTab={mainTab}>
        <TabPane tabId="orders">
          {/* Stats Card */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0">
                <CardBody className="p-4">
                  <div className="row g-3">
                    {/* Total Orders */}
                    <div className="col-md-4">
                      <div
                        className="stats-item p-3 rounded h-100"
                        style={{ backgroundColor: "#fafbfc" }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                            style={{
                              width: "40px",
                              height: "40px",
                              backgroundColor: "#e0f2fe",
                            }}
                          >
                            <Package size={18} className="text-primary" />
                          </div>
                          <div>
                            <span className="text-muted small d-block">
                              Total Orders
                            </span>
                            <span className="fw-bold h5 mb-0">
                              {customerData?.totalOrders || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Total Spent */}
                    <div className="col-md-4">
                      <div
                        className="stats-item p-3 rounded h-100"
                        style={{ backgroundColor: "#f0fdf4" }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                            style={{
                              width: "40px",
                              height: "40px",
                              backgroundColor: "#dcfce7",
                            }}
                          >
                            <DollarSign size={18} className="text-success" />
                          </div>
                          <div>
                            <span className="text-muted small d-block">
                              Total Spent
                            </span>
                            <span className="fw-bold h5 mb-0 text-success">
                              $
                              {customerData?.totalAmount
                                ? customerData.totalAmount.toFixed(2)
                                : "0.00"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Last Order */}
                    <div className="col-md-4">
                      <div
                        className="stats-item p-3 rounded h-100"
                        style={{ backgroundColor: "#fef7ff" }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                            style={{
                              width: "40px",
                              height: "40px",
                              backgroundColor: "#f3e8ff",
                            }}
                          >
                            <Calendar size={18} className="text-info" />
                          </div>
                          <div>
                            <span className="text-muted small d-block">
                              Last Order
                            </span>
                            <span className="fw-bold">
                              {customerData?.lastOrderDate
                                ? new Date(
                                    customerData.lastOrderDate
                                  ).toLocaleDateString("en-IN", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })
                                : "No orders yet"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Orders Section */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0">
                <CardBody className="p-0">
                  {/* Card Header */}
                  <div
                    className="px-4 py-2 border-bottom"
                    style={{ backgroundColor: "#fafbfc" }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <h5 className="mb-0 fw-bold text-dark d-flex align-items-center">
                        Order History
                      </h5>
                    </div>
                  </div>

                  <div className="p-4">
                    {/* Enhanced Tabs */}
                    <Nav
                      tabs
                      className="mb-4 nav-tabs-custom"
                      style={{
                        borderBottom: "2px solid #f1f5f9",
                      }}
                    >
                      <NavItem>
                        <NavLink
                          className={`nav-link-custom ${
                            activeTab === "all" ? "active" : ""
                          }`}
                          onClick={() => handleTabChange("all")}
                          style={{
                            cursor: "pointer",
                            padding: "12px 24px",
                            border: "none",
                            borderRadius: "8px 8px 0 0",
                            fontWeight: activeTab === "all" ? "600" : "500",
                            backgroundColor:
                              activeTab === "all" ? "#3b82f6" : "transparent",
                            color: activeTab === "all" ? "#ffffff" : "#64748b",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <Package size={16} className="me-2" />
                            All Orders
                            {ordersData.length > 0 && (
                              <span
                                className="badge ms-2"
                                style={{
                                  backgroundColor:
                                    activeTab === "all" ? "#1d4ed8" : "#e2e8f0",
                                  color:
                                    activeTab === "all" ? "#ffffff" : "#475569",
                                }}
                              >
                                {ordersData.length}
                              </span>
                            )}
                          </div>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={`nav-link-custom ${
                            activeTab === "cancelled" ? "active" : ""
                          }`}
                          onClick={() => handleTabChange("cancelled")}
                          style={{
                            cursor: "pointer",
                            padding: "12px 24px",
                            border: "none",
                            borderRadius: "8px 8px 0 0",
                            fontWeight:
                              activeTab === "cancelled" ? "600" : "500",
                            backgroundColor:
                              activeTab === "cancelled"
                                ? "#ef4444"
                                : "transparent",
                            color:
                              activeTab === "cancelled" ? "#ffffff" : "#64748b",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <XCircle size={16} className="me-2" />
                            Cancelled Orders
                            {cancelledOrdersData.length > 0 && (
                              <span
                                className="badge ms-2"
                                style={{
                                  backgroundColor:
                                    activeTab === "cancelled"
                                      ? "#dc2626"
                                      : "#e2e8f0",
                                  color:
                                    activeTab === "cancelled"
                                      ? "#ffffff"
                                      : "#475569",
                                }}
                              >
                                {cancelledOrdersData.length}
                              </span>
                            )}
                          </div>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={`nav-link-custom ${
                            activeTab === "topOrder" ? "active" : ""
                          }`}
                          onClick={() => handleTabChange("topOrder")}
                          style={{
                            cursor: "pointer",
                            padding: "12px 24px",
                            border: "none",
                            borderRadius: "8px 8px 0 0",
                            fontWeight:
                              activeTab === "topOrder" ? "600" : "500",
                            backgroundColor:
                              activeTab === "topOrder"
                                ? "#d3178a"
                                : "transparent",
                            color:
                              activeTab === "topOrder" ? "#ffffff" : "#64748b",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <BarChart3 size={16} className="me-2" />
                            Top Ordered
                            {cancelledOrdersData.length > 0 && (
                              <span
                                className="badge ms-2"
                                style={{
                                  backgroundColor:
                                    activeTab === "topOrder"
                                      ? "#dc2626"
                                      : "#e2e8f0",
                                  color:
                                    activeTab === "topOrder"
                                      ? "#ffffff"
                                      : "#475569",
                                }}
                              >
                                {cancelledOrdersData.length}
                              </span>
                            )}
                          </div>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={`nav-link-custom ${
                            activeTab === "topCategory" ? "active" : ""
                          }`}
                          onClick={() => handleTabChange("topCategory")}
                          style={{
                            cursor: "pointer",
                            padding: "12px 24px",
                            border: "none",
                            borderRadius: "8px 8px 0 0",
                            fontWeight:
                              activeTab === "topCategory" ? "600" : "500",
                            backgroundColor:
                              activeTab === "topCategory"
                                ? "#d3178a"
                                : "transparent",
                            color:
                              activeTab === "topCategory"
                                ? "#ffffff"
                                : "#64748b",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <BarChart3 size={16} className="me-2" />
                            Top Category
                            {cancelledOrdersData.length > 0 && (
                              <span
                                className="badge ms-2"
                                style={{
                                  backgroundColor:
                                    activeTab === "topCategory"
                                      ? "#dc2626"
                                      : "#e2e8f0",
                                  color:
                                    activeTab === "topCategory"
                                      ? "#ffffff"
                                      : "#475569",
                                }}
                              >
                                {cancelledOrdersData.length}
                              </span>
                            )}
                          </div>
                        </NavLink>
                      </NavItem>
                    </Nav>

                    {/* Tab Content */}
                    <TabContent activeTab={activeTab}>
                      <TabPane tabId="all">
                        {ordersLoading ? (
                          <div className="text-center py-5">
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="text-muted mt-3">Loading orders...</p>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <DataTable
                              data={ordersData}
                              columns={orderColumns}
                              pagination
                              paginationPerPage={10}
                              paginationRowsPerPageOptions={[10, 25, 50]}
                              noDataComponent={
                                <div className="text-center py-5">
                                  <div
                                    className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                                    style={{
                                      width: "80px",
                                      height: "80px",
                                      backgroundColor: "#f8fafc",
                                    }}
                                  >
                                    <Package size={36} className="text-muted" />
                                  </div>
                                  <h6 className="text-muted mb-2">
                                    No orders found
                                  </h6>
                                  <p className="text-muted small">
                                    This customer hasn't placed any orders yet.
                                  </p>
                                </div>
                              }
                              customStyles={{
                                headRow: {
                                  style: {
                                    backgroundColor: "#f8fafc",
                                    borderBottom: "2px solid #e2e8f0",
                                    fontWeight: "600",
                                  },
                                },
                                rows: {
                                  style: {
                                    borderBottom: "1px solid #f1f5f9",
                                    "&:hover": {
                                      backgroundColor: "#f8fafc",
                                    },
                                  },
                                },
                              }}
                              responsive
                            />
                          </div>
                        )}
                      </TabPane>

                      <TabPane tabId="cancelled">
                        {ordersLoading ? (
                          <div className="text-center py-5">
                            <div
                              className="spinner-border text-danger"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                            <p className="text-muted mt-3">
                              Loading cancelled orders...
                            </p>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <DataTable
                              data={cancelledOrdersData}
                              columns={orderColumns}
                              pagination
                              paginationPerPage={10}
                              paginationRowsPerPageOptions={[10, 25, 50]}
                              noDataComponent={
                                <div className="text-center py-5">
                                  <div
                                    className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                                    style={{
                                      width: "80px",
                                      height: "80px",
                                      backgroundColor: "#fef2f2",
                                    }}
                                  >
                                    <Package
                                      size={36}
                                      className="text-danger"
                                    />
                                  </div>
                                  <h6 className="text-muted mb-2">
                                    No cancelled orders
                                  </h6>
                                  <p className="text-muted small">
                                    This customer has no cancelled orders.
                                  </p>
                                </div>
                              }
                              customStyles={{
                                headRow: {
                                  style: {
                                    backgroundColor: "#fef2f2",
                                    borderBottom: "2px solid #fecaca",
                                    fontWeight: "600",
                                  },
                                },
                                rows: {
                                  style: {
                                    borderBottom: "1px solid #fee2e2",
                                    "&:hover": {
                                      backgroundColor: "#fef7f7",
                                    },
                                  },
                                },
                              }}
                              responsive
                            />
                          </div>
                        )}
                      </TabPane>
                      <TabPane tabId="topOrder">
                        {activeTab === "topOrder" && (
                          <TopBoughtProducts customerId={customerId} />
                        )}
                      </TabPane>
                      <TabPane tabId="topCategory">
                        {activeTab === "topCategory" && (
                          <TopBoughtCategorySubcategory
                            customerId={customerId}
                          />
                        )}
                      </TabPane>
                    </TabContent>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>
        <TabPane tabId="loyalty">
          {mainTab === "loyalty" && <LoyaltyHistory />}
        </TabPane>
      </TabContent>

      {/* Customer Tag Modal */}
      <CustomerTagModal
        isOpen={isTagModalOpen}
        toggle={() => setIsTagModalOpen(false)}
        customer={customerInfo}
        onTagsUpdated={handleTagsUpdated}
      />
    </Fragment>
  );
};

export default CustomerDetailPage;
